import {
  InputFieldsType,
  SelectOptionsType,
  getValidationRules,
} from '@composites/InboundButton/utils';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  CheckboxText,
  FormItemWrapper,
  FormItemsWrapper,
  HintText,
  RegistrationFormRoot,
} from './styles';

import Checkbox from '@components/Checkbox';
import { RowAlignedItems } from '@components/LinearLayout';
import WePhoneInput from '@components/WePhoneInput';
import { TextLink } from '@composites/EventsSection/styles';
import FormItem from '@composites/FormItem';
import InputSelect from '@components/InputSelect';
import WeInput from '@composites/WeInput';
import { ContactApproval } from '@pages/EventContactPage/components/ContactForm/styles';
import { getEventDetails } from '@store/slices/eventSlice';
import { useTabletWidth } from '@utils/hooks';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

interface RegistrationFormProps {
  control: Control<Record<string, string>, any>;
  inputElements: InputFieldsType;
  errors: FieldErrors<Record<string, string>>;
  setCheckedApproval: Dispatch<SetStateAction<boolean>>;
  checkedApproval: boolean;
  handleChange?: (fieldName: string) => (value: string) => void;
}

const RegistrationForm = ({
  control,
  errors,
  inputElements,
  checkedApproval,
  setCheckedApproval,
  handleChange,
}: RegistrationFormProps) => {
  // Provide a default no-op handleChange if not supplied
  // eslint-disable-next-line
  const safeHandleChange = handleChange || (() => () => {});
  const isMobile = useTabletWidth();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const sluggedEvent = useSelector(getEventDetails)?.[0];

  const normalizeId = (id?: string) =>
    (id || '').toLowerCase().replace(/[^a-z]/g, '');

  const isTopTrio = (id?: string) => {
    const n = normalizeId(id);
    return n === 'salutation' || n === 'firstname' || n === 'lastname';
  };

  return (
    <section>
      <RegistrationFormRoot>
        <FormItemsWrapper>
          {inputElements.map(item => {
            const width = isMobile
              ? `${(item.mobile / 12) * 100}%`
              : isTopTrio(item.id)
                ? 'calc((100% - 8%) / 3)'
                : `${(item.desktop / 12) * 100}%`;

            const rules = getValidationRules(item);
            const lang = (
              sluggedEvent?.language ||
              i18n.language ||
              'en'
            ).toLowerCase();
            const selectOptions =
              item.id === 'salutation'
                ? lang.startsWith('de')
                  ? // DE Mr., Ms., Mrs.
                    [
                      {
                        value: 'Mr.',
                        label: t('salutation-mapping.mr', 'Herr'),
                      },
                      {
                        value: 'Mrs.',
                        label: t('salutation-mapping.mrs', 'Frau'),
                      },
                    ]
                  : // EN: Mr., Ms., Mrs.
                    [
                      {
                        value: 'Mr.',
                        label: t('salutation-mapping.mr', 'Mr.'),
                      },
                      {
                        value: 'Ms.',
                        label: t('salutation-mapping.ms', 'Ms.'),
                      },
                      {
                        value: 'Mrs.',
                        label: t('salutation-mapping.mrs', 'Mrs.'),
                      },
                    ]
                : (item?.options ?? []).map(opt => ({
                    value: opt.value,
                    label: t(opt.label),
                  }));

            const formChild = () => {
              switch (item.type) {
                case 'input-select':
                  return (
                    <FormItem
                      gap="0.2rem"
                      label={`${t(item.id)}`}
                      labelColor={theme.color.mobilityGrey}
                      width="100%"
                      required={item.required}
                      paddingLabel="1rem"
                      error={
                        typeof errors[item.id]?.message === 'string'
                          ? t(errors[item.id]?.message ?? '')
                          : undefined
                      }
                    >
                      <Controller
                        control={control}
                        name={item.id}
                        rules={rules}
                        render={({ field }) => (
                          <Controller
                            control={control}
                            name={item.id}
                            rules={rules}
                            render={({ field }) => (
                              <InputSelect
                                options={selectOptions as SelectOptionsType}
                                placeholder={t('please-select')}
                                border={`0.4rem solid ${theme.color.strokeGrey}`}
                                borderRadius={theme.borderRadius.radius25}
                                fontSize={theme.fontSize.size14}
                                color={theme.color.mobilityGrey}
                                hoverColor={theme.color.mobilityGrey}
                                backgroundColor={theme.color.pureWhite}
                                minWidth="100%"
                                maxWidth="100%"
                                isSearchable
                                textAlign="left"
                                value={field.value}
                                onChange={(value: string) => {
                                  field.onChange(value);
                                  safeHandleChange(item.id)(value);
                                }}
                              />
                            )}
                          />
                        )}
                      />
                    </FormItem>
                  );

                case 'input-text':
                  return (
                    <FormItem
                      gap="0.2rem"
                      label={`${t(item.id)}`}
                      labelColor={theme.color.mobilityGrey}
                      width="100%"
                      required={item.required}
                      paddingLabel="1rem"
                      error={
                        typeof errors[item.id]?.message === 'string'
                          ? t(errors[item.id]?.message ?? '')
                          : undefined
                      }
                    >
                      <Controller
                        control={control}
                        name={item.id}
                        rules={rules}
                        render={({ field }) => (
                          <WeInput
                            placeholder={t(item.id)}
                            border={`0.4rem solid ${theme.color.strokeGrey}`}
                            focusBorder={`0.4rem solid ${sluggedEvent?.design?.themeColor}`}
                            borderRadius={theme.borderRadius.radius25}
                            fontSize={theme.fontSize.size14}
                            padding="1.2rem 2rem"
                            color={theme.color.mobilityGrey}
                            focusColor={theme.color.mobilityGrey}
                            backgroundColor={theme.color.pureWhite}
                            minWidth="100%"
                            maxWidth="100%"
                            height="4rem"
                            {...field}
                            onChange={(e: any) => {
                              field.onChange(e);
                              safeHandleChange(item.id)(e.target.value);
                            }}
                          />
                        )}
                      />
                    </FormItem>
                  );
                case 'phone-number':
                  return (
                    <FormItem
                      gap="0.2rem"
                      label={`${t(item.id)}`}
                      labelColor={theme.color.mobilityGrey}
                      width="100%"
                      required={item.required}
                      paddingLabel="1rem"
                      error={
                        typeof errors[item.id]?.message === 'string'
                          ? t(errors[item.id]?.message ?? '')
                          : undefined
                      }
                    >
                      <Controller
                        control={control}
                        name={item.id}
                        rules={rules}
                        render={({ field }) => (
                          <WePhoneInput
                            {...field}
                            className="registration-form"
                            borderColor={theme.color.strokeGrey}
                            focusBorderColor={sluggedEvent?.design?.themeColor}
                            onChange={(value: string) => {
                              field.onChange(value);
                              safeHandleChange(item.id)(value);
                            }}
                          />
                        )}
                      />
                    </FormItem>
                  );

                default:
                  return null;
              }
            };

            return (
              <FormItemWrapper key={item.id} $width={width}>
                {formChild()}
              </FormItemWrapper>
            );
          })}

          <HintText>*{t('required-fields')}</HintText>
        </FormItemsWrapper>
        <ContactApproval>
          <Checkbox
            label={
              <RowAlignedItems>
                <CheckboxText $required>
                  {t('inbound-text-approval-1')}
                  <Trans i18nKey="inbound-text-approval-2">
                    Further information can be found in our
                    <TextLink
                      href={t('form.privacy-url')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      privacy policy
                    </TextLink>
                    .
                  </Trans>
                </CheckboxText>
              </RowAlignedItems>
            }
            spacing={theme.spacing.size10}
            checked={checkedApproval}
            type="square"
            size="3.2rem"
            backgroundColor={theme.color.white}
            color={theme.color.mobilityGrey}
            borderRadius={theme.borderRadius.radius5}
            alignItems="start"
            border={`0.4rem solid ${theme.color.strokeGrey}`}
            onChange={() => {
              setCheckedApproval(prev => !prev);
            }}
          />
        </ContactApproval>
      </RegistrationFormRoot>
    </section>
  );
};

export default RegistrationForm;
